import React, { useMemo, useState } from 'react';

const SortIcon = ({ direction }) => (
  <svg className="w-3 h-3 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
    ) : (
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
    )}
  </svg>
);

function formatSignalTime(unixMs) {
  if (!unixMs) return null;
  const d = new Date(unixMs);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function BreakoutTable({ title, signals, tickers, defaultSortDir = 'asc' }) {
  const [sortCol, setSortCol] = useState('change'); // 'change' or 'time'
  const [sortDir, setSortDir] = useState(defaultSortDir);

  const rows = useMemo(() => {
    return signals
      .map((sig) => {
        const live = tickers[sig.symbol];
        const price = live ? live.price : sig.price || null;
        let changePercent = null;
        const refOpen = sig.ref_open || 0;
        if (price && refOpen > 0) {
          changePercent = ((price - refOpen) / refOpen) * 100;
        }
        return { ...sig, livePrice: price, changePercent };
      })
      .sort((a, b) => {
        if (sortCol === 'time') {
          const aTime = a.signal_time || 0;
          const bTime = b.signal_time || 0;
          return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
        }
        // sort by change %
        if (a.changePercent == null && b.changePercent == null) return 0;
        if (a.changePercent == null) return 1;
        if (b.changePercent == null) return -1;
        return sortDir === 'asc'
          ? a.changePercent - b.changePercent
          : b.changePercent - a.changePercent;
      });
  }, [signals, tickers, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">{title}</h2>
          <span className="text-xs text-gray-500">
            {rows.length} {rows.length === 1 ? 'ticker' : 'tickers'}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900 z-10">
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortCol === 'change' ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('change')}
              >
                Change %{sortCol === 'change' && <SortIcon direction={sortDir} />}
              </th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortCol === 'time' ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('time')}
              >
                Time{sortCol === 'time' && <SortIcon direction={sortDir} />}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 sm:px-6 py-12 text-center">
                  <span className="text-sm text-gray-500">No signals yet</span>
                </td>
              </tr>
            ) : rows.map((row) => {
                const hasPrice = row.livePrice != null;
                const isPositive = row.changePercent >= 0;
                const timeStr = formatSignalTime(row.signal_time);
                return (
                  <tr
                    key={row.symbol}
                    onClick={() => openTradingView(row.symbol)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{row.symbol}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {hasPrice ? (
                        <span className="text-sm text-gray-300 font-medium tabular-nums">
                          ${row.livePrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.changePercent != null ? (
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {isPositive ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {isPositive ? '+' : ''}
                          {row.changePercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {timeStr ? (
                        <span className="text-sm text-gray-400 tabular-nums">{timeStr}</span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BreakoutTables({ longs, shorts, tickers }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <BreakoutTable title="Long (Above 9 EMA)" signals={longs} tickers={tickers} defaultSortDir="desc" />
      <BreakoutTable title="Short (Below 9 EMA)" signals={shorts} tickers={tickers} />
    </div>
  );
}
